import { useEffect, useRef } from "react";

const FFTVisualizer = ({ isPlaying, analyzerRef, pc, sc, maxWidth = 576 }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const barsRef = useRef([]);
  const peaksRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.min(rect.width, maxWidth);
      const h = 220;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.getContext("2d").scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const BAR_COUNT = 64;
    if (barsRef.current.length !== BAR_COUNT) {
      barsRef.current = new Array(BAR_COUNT).fill(0);
      peaksRef.current = new Array(BAR_COUNT).fill(0);
    }

    const draw = () => {
      const ctx = canvas.getContext("2d");
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, w, h);

      const gap = 3;
      const barW = (w - gap * (BAR_COUNT - 1)) / BAR_COUNT;
      const mainH = h * 0.75;
      const reflectH = h * 0.25;

      let freqData = null;
      if (isPlaying && analyzerRef?.current) {
        try {
          const buf = new Uint8Array(analyzerRef.current.frequencyBinCount);
          analyzerRef.current.getByteFrequencyData(buf);
          freqData = buf;
        } catch {}
      }
      const hasReal = freqData && Array.from(freqData).some(v => v > 10);

      for (let i = 0; i < BAR_COUNT; i++) {
        let target;
        if (hasReal) {
          const idx = Math.floor(i * (freqData.length / BAR_COUNT));
          target = (freqData[idx] || 0) / 255;
        } else if (isPlaying) {
          target = Math.random() * 0.6 + 0.1;
        } else {
          target = 0.03;
        }

        // Smooth animation
        barsRef.current[i] += (target - barsRef.current[i]) * 0.25;
        const val = barsRef.current[i];
        const barH = Math.max(3, val * mainH * 0.95);

        // Peak hold
        if (val > peaksRef.current[i]) {
          peaksRef.current[i] = val;
        } else {
          peaksRef.current[i] -= 0.008;
          if (peaksRef.current[i] < 0) peaksRef.current[i] = 0;
        }

        const x = i * (barW + gap);
        const y = mainH - barH;

        // Main bar
        const barColor = pc || "#EF4444";
        ctx.fillStyle = barColor;
        ctx.beginPath();
        ctx.roundRect(x, y, barW, barH, 2);
        ctx.fill();

        // Peak indicator
        const peakY = mainH - peaksRef.current[i] * mainH * 0.95;
        ctx.fillStyle = barColor + "BB";
        ctx.fillRect(x, peakY - 2, barW, 2);

        // Reflection (mirrored, faded)
        const reflBarH = barH * 0.4;
        const reflGrad = ctx.createLinearGradient(x, mainH, x, mainH + reflBarH);
        reflGrad.addColorStop(0, barColor + "40");
        reflGrad.addColorStop(1, barColor + "00");
        ctx.fillStyle = reflGrad;
        ctx.beginPath();
        ctx.roundRect(x, mainH + 2, barW, reflBarH, 2);
        ctx.fill();
      }

      // Divider line
      ctx.strokeStyle = (pc || "#EF4444") + "30";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, mainH);
      ctx.lineTo(w, mainH);
      ctx.stroke();

      // Frequency labels
      ctx.fillStyle = "#52525b";
      ctx.font = "9px monospace";
      ctx.textAlign = "center";
      const labels = ["60Hz", "250Hz", "1kHz", "4kHz", "16kHz"];
      labels.forEach((lbl, li) => {
        const lx = (li / (labels.length - 1)) * w;
        ctx.fillText(lbl, lx, h - 4);
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      window.removeEventListener("resize", resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isPlaying, analyzerRef, pc, sc, maxWidth]);

  return (
    <div className="w-full flex justify-center" data-testid="fft-visualizer">
      <canvas
        ref={canvasRef}
        style={{ opacity: isPlaying ? 1 : 0.3, transition: "opacity 0.5s ease", maxWidth: `${maxWidth}px`, width: "100%" }}
      />
    </div>
  );
};

export default FFTVisualizer;
